import { Injectable } from '@angular/core';
import { CreationToolError } from '@App/app/shared/exceptions/creation-tool-error';
import { Plane, Ray, Vector3 } from 'babylonjs';
import { modelMeshPredicate } from '../../../helpers/layers-helpers';
import { SceneService } from '../../scene-service/scene.service';
import { AngleIronMathService } from './angle-iron-math.service';

@Injectable({
  providedIn: 'root',
})
export class AngleIronRaycastService {
  constructor(private sceneService: SceneService, private mathService: AngleIronMathService) {}

  pickWithRays(rays: Ray[]): (Vector3 | null | undefined)[] {
    return rays.map(
      (ray) => this.sceneService.scene.pickWithRay(ray, modelMeshPredicate)?.pickedPoint,
    );
  }

  findBoxEdgePoint(center: Vector3, direction: Vector3): Vector3 {
    const ray = new Ray(center, direction, 100);
    const pick = this.sceneService.scene.pickWithRay(ray, modelMeshPredicate)?.pickedPoint;
    if (!pick) {
      throw new CreationToolError('Mesh edges not find');
    }
    return pick;
  }

  findLengthEdges(
    points: [Vector3, Vector3],
    planes: [Plane, Plane],
    cross: Vector3,
  ): [Vector3, Vector3] {
    const edges = points.map((point, i) => {
      const center = this.mathService.findCenterPoint(point, planes[Math.abs(i - 1)]);
      const rays = [new Ray(center, cross, 100), new Ray(center, cross.negate(), 100)];
      return this.pickWithRays(rays);
    }) as [[Vector3, Vector3], [Vector3, Vector3]];
    return [Vector3.Center(edges[0][0], edges[1][0]), Vector3.Center(edges[0][1], edges[1][1])];
  }

  findThickness(
    points: [Vector3, Vector3],
    projected: [Vector3, Vector3],
    normals: [Vector3, Vector3],
    planes: [Plane, Plane],
    cross: Vector3,
  ): number {
    const edges = points.map((point, i) => {
      const pick = this.findBoxEdgePoint(point, normals[i]);
      return this.mathService.doubleProjectOnPlanes(pick, [
        planes[Math.abs(i - 1)],
        Plane.FromPositionAndNormal(projected[Math.abs(i - 1)], cross),
      ]);
    });
    return (
      (Vector3.Distance(edges[0], projected[1]) + Vector3.Distance(edges[1], projected[0])) /
      edges.length
    );
  }
}
